import { apiClient } from '../../utils/apiClient';
import { logger } from '../../utils/logger';
import {
  API_EDI_PARTY_ASSIGNMENTS_LIST,
  API_EDI_PARTY_ASSIGNMENTS_CREATE,
  API_EDI_PARTY_ASSIGNMENTS_GET,
  API_EDI_PARTY_ASSIGNMENTS_UPDATE,
  API_EDI_PARTY_ASSIGNMENTS_DELETE,
  API_USERS_UPDATE_AFFILIATION,
} from '../../constants/apiConstants';

// Status codes mirror SmartMatrix.Domain.Core.Parties.Messages
// .SysUserPartyAssignment_Create_Response.StatusCodes — kept here so the FE
// can branch on the Conflict path without re-importing the BE constants.
export const STATUS_PARTY_ASSIGNMENT_CONFLICT = -7004;

export interface UserPartyAssignment {
  id: number;
  partitionKey: string;
  tenantId: number;
  sysUserId: number;
  partyId: number;
  partyRole: string;
  sysRoleId: number;
  roleCode?: string | null;
  roleName?: string | null;
  isActive: boolean;
  status: string;
  createdAt?: string | null;
  createdBy?: string | null;
  modifiedAt?: string | null;
  modifiedBy?: string | null;
}

export interface UserPartyAssignmentListParams {
  tenantId: number;
  sysUserId?: number;
  partyId?: number;
  partyRole?: string;
  sysRoleId?: number;
  isActive?: boolean;
  pageIndex?: number;
  pageSize?: number;
}

export interface UserPartyAssignmentListResponse {
  items: UserPartyAssignment[];
  pageIndex: number;
  pageSize: number;
  totalCount: number;
}

export interface UserPartyAssignmentCreateRequest {
  sysUserId: number;
  partyId: number;
  sysRoleId: number;
}

export interface UserPartyAssignmentUpdateRequest {
  sysRoleId?: number;
  isActive?: boolean;
}

interface ResultEnvelope<T> {
  succeeded: boolean;
  statusCode?: number;
  messages?: string[];
  data?: T;
}

// Inner BE response payloads we care about. Property names map straight from
// the C# DTOs via System.Text.Json default camelCase.
interface CreateInner { assignmentId: number; message: string; }
interface UpdateInner { assignmentId: number; message: string; }
interface DeleteInner { assignmentId: number; message: string; }
interface GetByIdInner { item: UserPartyAssignment | null; message: string; }
interface UpdateAffiliationInner { sysUserId: number; affiliationType: string; message: string; }

/**
 * Thrown when Create resolves to a Conflict — another active assignment exists
 * for the same (user, party) pair with a different role. Caller must explicitly
 * Update or Delete first; refuses silent overwrite per Q6 conflict resolution.
 */
export class PartyAssignmentConflictError extends Error {
  readonly reasonCode = 'PARTY_ASSIGNMENT_CONFLICT';
  constructor(message?: string) {
    super(message ?? 'A different role is already assigned for this user and party.');
    this.name = 'PartyAssignmentConflictError';
  }
}

export const userPartyAssignmentService = {
  async list(params: UserPartyAssignmentListParams): Promise<UserPartyAssignmentListResponse> {
    try {
      const response = await apiClient.get<ResultEnvelope<UserPartyAssignmentListResponse>>(
        API_EDI_PARTY_ASSIGNMENTS_LIST,
        {
          params: {
            tenantId: params.tenantId,
            sysUserId: params.sysUserId,
            partyId: params.partyId,
            partyRole: params.partyRole,
            sysRoleId: params.sysRoleId,
            isActive: params.isActive,
            pageIndex: params.pageIndex ?? 1,
            pageSize: params.pageSize ?? 50,
          },
        }
      );
      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.join(', ') || 'Failed to list party assignments');
      }
      return response.data.data ?? { items: [], pageIndex: 1, pageSize: 50, totalCount: 0 };
    } catch (error) {
      logger.error('Failed to list party assignments', error);
      throw error;
    }
  },

  async getById(tenantId: number, id: number): Promise<UserPartyAssignment | null> {
    const response = await apiClient.get<ResultEnvelope<GetByIdInner>>(
      API_EDI_PARTY_ASSIGNMENTS_GET(id),
      { params: { tenantId } }
    );
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.join(', ') || 'Failed to fetch assignment');
    }
    return response.data.data?.item ?? null;
  },

  async create(tenantId: number, request: UserPartyAssignmentCreateRequest): Promise<{ assignmentId: number }> {
    const response = await apiClient.post<ResultEnvelope<CreateInner>>(
      `${API_EDI_PARTY_ASSIGNMENTS_CREATE}?tenantId=${tenantId}`,
      request
    );
    if (!response.data.succeeded) {
      if (response.data.statusCode === STATUS_PARTY_ASSIGNMENT_CONFLICT) {
        throw new PartyAssignmentConflictError(response.data.messages?.[0]);
      }
      throw new Error(response.data.messages?.join(', ') || 'Failed to create assignment');
    }
    return { assignmentId: response.data.data?.assignmentId ?? 0 };
  },

  async update(
    tenantId: number,
    id: number,
    request: UserPartyAssignmentUpdateRequest
  ): Promise<{ assignmentId: number }> {
    const response = await apiClient.patch<ResultEnvelope<UpdateInner>>(
      `${API_EDI_PARTY_ASSIGNMENTS_UPDATE(id)}?tenantId=${tenantId}`,
      request
    );
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.join(', ') || 'Failed to update assignment');
    }
    return { assignmentId: response.data.data?.assignmentId ?? id };
  },

  async delete(tenantId: number, id: number): Promise<{ assignmentId: number }> {
    const response = await apiClient.delete<ResultEnvelope<DeleteInner>>(
      `${API_EDI_PARTY_ASSIGNMENTS_DELETE(id)}?tenantId=${tenantId}`
    );
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.join(', ') || 'Failed to delete assignment');
    }
    return { assignmentId: response.data.data?.assignmentId ?? id };
  },

  /**
   * PATCH a user's affiliation_type. Lives here (rather than userService) since
   * the endpoint is gated on `edi.party-members.manage` and the FE call
   * site is the EDI Admin / user-edit modal — same domain as assignments.
   */
  async updateUserAffiliation(userId: number, affiliationType: string): Promise<{ affiliationType: string }> {
    const response = await apiClient.patch<ResultEnvelope<UpdateAffiliationInner>>(
      API_USERS_UPDATE_AFFILIATION(userId),
      { affiliationType }
    );
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.join(', ') || 'Failed to update affiliation');
    }
    return { affiliationType: response.data.data?.affiliationType ?? affiliationType };
  },
};
